import { cn } from "@base/lib/utils";
import { useRerenderAfterMount } from "@base/model/hooks/useRerenderAfterMount";
import { Button, Checkbox, DatePicker, Radio, TextField } from "@buildblockinc/buildblock-design-system";
import { useState } from "react";
import type { DateDraft, DatePresetMode } from "../model/dailyLogSearchFilterDraft";
import type { FilterOption } from "../model/dailyLogSearchFilterOptions";
import { FILTER_IDS_MAX, normalizeIdList } from "../model/dailyLogSearchState";

const DATE_PRESETS: { mode: DatePresetMode; label: string; needsToday: boolean }[] = [
  { mode: "all", label: "All time", needsToday: false },
  { mode: "recent", label: "Last 7 days", needsToday: true },
  { mode: "month", label: "This month", needsToday: true },
  { mode: "custom", label: "Custom range", needsToday: false },
];

interface DateFilterFieldsProps {
  draft: DateDraft;
  onChange: (draft: DateDraft) => void;
  /** 프로젝트 timezone 기준 오늘. 알 수 없으면 빠른 선택을 비활성화한다 */
  today: string | null;
  onRetryToday?: () => void;
}

export function DateFilterFields({ draft, onChange, today, onRetryToday }: DateFilterFieldsProps) {
  // 디자인 시스템 Radio가 첫 렌더에 지정 색(secondary) 대신 기본 보라색으로 그려지는 문제 우회
  useRerenderAfterMount();
  const selectPreset = (mode: DatePresetMode) => {
    if (mode === "custom") {
      onChange({ mode: "custom", startDate: "", endDate: "" });
      return;
    }
    onChange({ mode });
  };

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-2 text-label2 text-gray-1000">Date</legend>
      <Radio
        size="sm"
        color="secondary"
        value={draft.mode}
        onValueChange={({ value }) => {
          if (isDatePresetMode(value)) selectPreset(value);
        }}
        className="flex flex-col gap-2 text-body4 text-gray-900"
      >
        {DATE_PRESETS.map((preset) => (
          <Radio.Item
            key={preset.mode}
            value={preset.mode}
            label={preset.label}
            disabled={preset.needsToday && today === null}
          />
        ))}
      </Radio>

      {today === null && (
        <output className="block text-label3 text-gray-700">
          Couldn't load the project's time zone, so quick ranges are unavailable.{" "}
          {onRetryToday && (
            <Button type="button" variant="ghost" color="info" size="small" onClick={onRetryToday}>
              Retry
            </Button>
          )}
        </output>
      )}

      {draft.mode === "custom" && <CustomRangeFields draft={draft} onChange={onChange} today={today} />}
    </fieldset>
  );
}

function isDatePresetMode(value: string | null): value is DatePresetMode {
  return DATE_PRESETS.some((preset) => preset.mode === value);
}

type CustomDateDraft = Extract<DateDraft, { mode: "custom" }>;

interface CustomRangeFieldsProps {
  draft: CustomDateDraft;
  onChange: (draft: DateDraft) => void;
  today: string | null;
}

/**
 * 직접 기간. 작성 화면·Work History와 같은 디자인 시스템 DatePicker(range)를 쓴다.
 * 달력은 portal 없이 필터 창 안에 렌더한다 — portal로 띄우면 달력 클릭이 필터 popover의 바깥 클릭으로 처리되거나
 * 모바일 필터 panel(z-overlay) 뒤로 가려진다.
 */
function CustomRangeFields({ draft, onChange, today }: CustomRangeFieldsProps) {
  const value = [draft.startDate, draft.endDate].filter((date) => date !== "");

  return (
    <DatePicker
      selectionMode="range"
      color="secondary"
      value={value}
      onValueChange={({ valueAsString: [startDate = "", endDate = ""] }) => onChange({ ...draft, startDate, endDate })}
      // 미래 날짜 수동 선택은 API가 막지 않지만(api-contract §2.2), 기본 선택 범위는 프로젝트 기준 오늘까지로 둔다.
      isDateUnavailable={(date) => today !== null && date.toString() > today}
    >
      <DatePicker.Label>Custom range</DatePicker.Label>
      <DatePicker.Input placeholder="Choose start & end date" />
      <DatePicker.Wrapper usePortal={false} className="z-10">
        <DatePicker.YearView />
        <DatePicker.MonthView />
        <DatePicker.DayView />
      </DatePicker.Wrapper>
    </DatePicker>
  );
}

interface OptionFilterFieldsProps {
  label: "Contractors" | "Status";
  options: FilterOption[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

/** 프로젝트 전체 선택지를 받아 로컬에서 이름을 좁힌다. 필터에서 새 항목을 만들지 않는다 */
export function OptionFilterFields({
  label,
  options,
  isLoading,
  isError,
  onRetry,
  selectedIds,
  onChange,
}: OptionFilterFieldsProps) {
  const [nameQuery, setNameQuery] = useState("");
  const normalizedQuery = nameQuery.trim().toLocaleLowerCase();
  const visibleOptions = (options ?? []).filter((option) => option.name.toLocaleLowerCase().includes(normalizedQuery));
  const selectedIdSet = new Set(selectedIds);
  const isAtLimit = selectedIds.length >= FILTER_IDS_MAX;

  const toggle = (id: number, checked: boolean) => {
    const next = checked ? [...selectedIds, id] : selectedIds.filter((selectedId) => selectedId !== id);
    onChange(normalizeIdList(next));
  };

  return (
    <fieldset className="flex flex-col gap-2 min-w-0">
      <legend className="mb-2 text-label2 text-gray-1000">{label}</legend>

      {isError && (
        <p className="text-label3 text-gray-700" role="alert">
          {`Couldn't load ${label.toLowerCase()}.`}{" "}
          <Button type="button" variant="ghost" color="info" size="small" onClick={onRetry}>
            Retry
          </Button>
        </p>
      )}
      {isLoading && <p className="text-label3 text-gray-700">{`Loading ${label.toLowerCase()}…`}</p>}

      {options && (
        <>
          <TextField>
            <TextField.Input
              size="medium"
              aria-label={`Find ${label.toLowerCase()}`}
              placeholder={`Find ${label.toLowerCase()}`}
              value={nameQuery}
              onChange={(event) => setNameQuery(event.target.value)}
            />
          </TextField>
          <p className="text-label3 text-gray-700" aria-live="polite">
            {isAtLimit
              ? `You can select up to ${FILTER_IDS_MAX}.`
              : `${selectedIds.length} selected · up to ${FILTER_IDS_MAX}`}
          </p>
          <ul className="flex overflow-y-auto flex-col gap-1 max-h-60">
            {visibleOptions.length === 0 && (
              <li className="py-2 text-body4 text-gray-600">{`No ${label.toLowerCase()} found.`}</li>
            )}
            {visibleOptions.map((option) => {
              const isChecked = selectedIdSet.has(option.id);
              return (
                <li key={option.id}>
                  <FilterOptionCheckbox
                    name={option.name}
                    checked={isChecked}
                    disabled={!isChecked && isAtLimit}
                    onCheckedChange={(checked) => toggle(option.id, checked)}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </fieldset>
  );
}

interface FilterOptionCheckboxProps {
  name: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function FilterOptionCheckbox({ name, checked, disabled, onCheckedChange }: FilterOptionCheckboxProps) {
  // 디자인 시스템 Checkbox.Control은 크기·색을 렌더 도중 DOM(root의 data-size/data-color)에서 읽어,
  // 읽지 못한 렌더에서는 기본값(md, 보라색 primary)으로 그려진다. 마운트 직후 다시 렌더하고,
  // 그래도 남는 경우를 막도록 체크 상태 색·크기를 선택자 우선순위가 더 높은 규칙으로 고정한다.
  useRerenderAfterMount();

  return (
    <Checkbox
      size="sm"
      color="secondary"
      checked={checked}
      disabled={disabled}
      onCheckedChange={(details) => onCheckedChange(details.checked === true)}
      className={cn(
        "flex gap-2 items-start py-1",
        "[&_[data-part=control]]:w-4 [&_[data-part=control]]:h-4",
        "[&_[data-part=control][data-state=checked]:not([data-disabled])]:bg-blue-500",
        "[&_[data-part=control][data-state=checked]:not([data-disabled])]:border-none",
      )}
    >
      <Checkbox.Control />
      <Checkbox.Label>
        <span className="text-body4 text-gray-900 break-words [overflow-wrap:anywhere]">{name}</span>
      </Checkbox.Label>
    </Checkbox>
  );
}
